import type { Analysis } from './analyze';
import { pageItems, type Colour, type Item } from './drawing';
import { SHEET } from './geometry';
import type { Project } from './types';

/*
 * DXF R12 (AC1009) writer — the most widely readable DXF flavour (AutoCAD,
 * BricsCAD, LibreCAD, DraftSight, EPLAN's own DXF import). Text is encoded in
 * Windows-1254 so Turkish characters survive ($DWGCODEPAGE = ANSI_1254).
 * Units are mm; y is flipped because DXF's y axis points up.
 */

const LAYERS: Record<string, number> = {
  CERCEVE: 8,
  SEMBOL: 7,
  METIN: 7,
  ETIKET: 7,
  ACIKLAMA: 8,
  UC_NO: 8,
  TEL_NO: 4,
  KONTAK_AYNA: 8,
  REFERANS: 4,
  BAGLANTI: 7,
  BAGLANTI_L: 1,
  BAGLANTI_N: 5,
  BAGLANTI_PE: 3,
  BAGLANTI_DCP: 30,
  BAGLANTI_DCN: 6,
};
const WIRE_LAYER: Partial<Record<Colour, string>> = { pl: 'BAGLANTI_L', pn: 'BAGLANTI_N', ppe: 'BAGLANTI_PE', pdp: 'BAGLANTI_DCP', pdn: 'BAGLANTI_DCN' };

function layerOf(it: Item): string {
  switch (it.layer) {
    case 'frame': return 'CERCEVE';
    case 'symbol': return 'SEMBOL';
    case 'ref': return 'REFERANS';
    case 'label': return it.k === 'text' ? 'METIN' : 'SEMBOL';
    case 'tag': return 'ETIKET';
    case 'desc': return 'ACIKLAMA';
    case 'pin': return 'UC_NO';
    case 'wno': return 'TEL_NO';
    case 'mirror': return 'KONTAK_AYNA';
    case 'underlay': return 'ALTLIK';
    case 'wire': {
      const c: Colour = (it.k === 'circle' ? it.fill : it.k === 'poly' ? it.stroke : it.k === 'text' ? it.colour : undefined) ?? 'ink';
      return WIRE_LAYER[c] ?? 'BAGLANTI';
    }
  }
}

const f = (n: number) => (Math.round(n * 1000) / 1000).toString();

export interface DxfOptions {
  /** page indexes to export; placed side by side with a 20 mm gap */
  pages: number[];
  netNumbers?: boolean;
  parts?: import('./catalog').PartLookup;
  fronts?: import('./front').FrontLookup;
}

export function projectToDxfText(pr: Project, A: Analysis, o: DxfOptions): string {
  const L: string[] = [];
  const g = (code: number, v: string | number) => L.push(String(code), typeof v === 'number' ? f(v) : v);
  const pitch = SHEET.W + 20;

  g(0, 'SECTION'); g(2, 'HEADER');
  g(9, '$ACADVER'); g(1, 'AC1009');
  g(9, '$DWGCODEPAGE'); g(3, 'ANSI_1254');
  g(9, '$INSBASE'); g(10, 0); g(20, 0); g(30, 0);
  g(9, '$EXTMIN'); g(10, 0); g(20, 0); g(30, 0);
  g(9, '$EXTMAX'); g(10, pitch * o.pages.length); g(20, SHEET.H); g(30, 0);
  g(9, '$LTSCALE'); g(40, 1);
  g(0, 'ENDSEC');

  g(0, 'SECTION'); g(2, 'TABLES');
  g(0, 'TABLE'); g(2, 'LTYPE'); g(70, 2);
  g(0, 'LTYPE'); g(2, 'CONTINUOUS'); g(70, 0); g(3, 'Solid line'); g(72, 65); g(73, 0); g(40, 0);
  g(0, 'LTYPE'); g(2, 'KESIK'); g(70, 0); g(3, '__ __ __'); g(72, 65); g(73, 2); g(40, 2); g(49, 1.1); g(49, -0.9);
  g(0, 'ENDTAB');
  g(0, 'TABLE'); g(2, 'LAYER'); g(70, Object.keys(LAYERS).length);
  for (const [name, colour] of Object.entries(LAYERS)) {
    g(0, 'LAYER'); g(2, name); g(70, 0); g(62, colour); g(6, 'CONTINUOUS');
  }
  g(0, 'ENDTAB');
  g(0, 'TABLE'); g(2, 'STYLE'); g(70, 1);
  g(0, 'STYLE'); g(2, 'STANDARD'); g(70, 0); g(40, 0); g(41, 1); g(50, 0); g(71, 0); g(42, 2.5); g(3, 'txt'); g(4, '');
  g(0, 'ENDTAB');
  g(0, 'ENDSEC');

  g(0, 'SECTION'); g(2, 'ENTITIES');
  o.pages.forEach((pi, n) => {
    const ox = n * pitch;
    const X = (x: number) => ox + x, Y = (y: number) => SHEET.H - y;
    for (const it of pageItems(pr, A, pi, { netNumbers: o.netNumbers, parts: o.parts, fronts: o.fronts })) {
      if (it.k === 'image') {
        // DXF R12 cannot embed pictures: draw the picture's frame instead
        const x = X(it.x), y = Y(it.y), x2 = X(it.x + it.w), y2 = Y(it.y + it.h);
        for (const [a, b, c, d] of [[x, y, x2, y], [x2, y, x2, y2], [x2, y2, x, y2], [x, y2, x, y]]) {
          g(0, 'LINE'); g(8, 'SEMBOL'); g(10, a); g(20, b); g(30, 0); g(11, c); g(21, d); g(31, 0);
        }
        continue;
      }
      const layer = layerOf(it);
      if (it.k === 'poly') {
        if (it.fill && it.closed && it.pts.length >= 3) {
          // filled triangles/quads (arrow heads) as SOLID
          const p = it.pts, q = p.length >= 4 ? p[3] : p[2];
          g(0, 'SOLID'); g(8, layer);
          g(10, X(p[0][0])); g(20, Y(p[0][1])); g(30, 0);
          g(11, X(p[1][0])); g(21, Y(p[1][1])); g(31, 0);
          g(12, X(q[0])); g(22, Y(q[1])); g(32, 0);
          g(13, X(p[2][0])); g(23, Y(p[2][1])); g(33, 0);
          continue;
        }
        const pts = it.closed ? [...it.pts, it.pts[0]] : it.pts;
        for (let i = 0; i < pts.length - 1; i++) {
          g(0, 'LINE'); g(8, layer);
          if (it.dash) g(6, 'KESIK');
          g(10, X(pts[i][0])); g(20, Y(pts[i][1])); g(30, 0);
          g(11, X(pts[i + 1][0])); g(21, Y(pts[i + 1][1])); g(31, 0);
        }
      } else if (it.k === 'circle') {
        if (it.fill && !it.stroke) {
          // junction dot: a zero-length polyline with width renders as a filled dot
          g(0, 'POLYLINE'); g(8, layer); g(66, 1); g(10, 0); g(20, 0); g(30, 0); g(70, 1); g(40, it.r); g(41, it.r);
          g(0, 'VERTEX'); g(8, layer); g(10, X(it.c[0] - it.r / 2)); g(20, Y(it.c[1])); g(42, 1);
          g(0, 'VERTEX'); g(8, layer); g(10, X(it.c[0] + it.r / 2)); g(20, Y(it.c[1])); g(42, 1);
          g(0, 'SEQEND'); g(8, layer);
        } else {
          g(0, 'CIRCLE'); g(8, layer); g(10, X(it.c[0])); g(20, Y(it.c[1])); g(30, 0); g(40, it.r);
        }
      } else if (it.text) {
        const just = it.anchor === 'middle' ? 1 : it.anchor === 'end' ? 2 : 0;
        g(0, 'TEXT'); g(8, layer);
        g(10, X(it.x)); g(20, Y(it.y)); g(30, 0);
        g(40, it.size * 0.72); // cap height ≈ 0.72 × font size
        g(1, it.text.replace(/\r?\n/g, ' '));
        if (it.rot) g(50, -it.rot);
        g(7, 'STANDARD');
        if (just) {
          g(72, just);
          g(11, X(it.x)); g(21, Y(it.y)); g(31, 0);
        }
      }
    }
  });
  g(0, 'ENDSEC');
  g(0, 'EOF');
  return L.join('\r\n') + '\r\n';
}

/* Windows-1254 (Turkish) encoder for the characters that differ from Latin-1. */
const CP1254: Record<string, number> = {
  'Ğ': 0xd0, 'İ': 0xdd, 'Ş': 0xde, 'ğ': 0xf0, 'ı': 0xfd, 'ş': 0xfe,
  '€': 0x80, '‚': 0x82, 'ƒ': 0x83, '„': 0x84, '…': 0x85, '†': 0x86, '‡': 0x87, 'ˆ': 0x88, '‰': 0x89, 'Š': 0x8a,
  '‹': 0x8b, 'Œ': 0x8c, '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97, '˜': 0x98,
  '™': 0x99, 'š': 0x9a, '›': 0x9b, 'œ': 0x9c, 'Ÿ': 0x9f, '−': 0x2d,
};
const CP1254_UNUSED = new Set([0xd0, 0xdd, 0xde, 0xf0, 0xfd, 0xfe]);

export function encodeCp1254(s: string): Uint8Array {
  const out = new Uint8Array(s.length);
  let n = 0;
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    let b = CP1254[ch];
    if (b === undefined) b = c < 0x80 || (c >= 0xa0 && c <= 0xff && !CP1254_UNUSED.has(c)) ? c : 0x3f;
    out[n++] = b;
  }
  return out.slice(0, n);
}

export function projectToDxf(pr: Project, A: Analysis, o: DxfOptions): Uint8Array {
  return encodeCp1254(projectToDxfText(pr, A, o));
}
